import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { SDialog } from '@modules/ui-components';
import {
  BRAND_STYLE_REFERENCE_LIMIT,
  REFERENCE_ROLES,
  defaultReferenceInstruction,
  defaultReferenceRole,
  getDesignProfileOption,
  referenceSlotHint,
  type BrandStyleKitReferenceDraft,
} from './BrandStyleKitEditor';

export type ReferenceSetImportItem = {
  file: File;
  draft: BrandStyleKitReferenceDraft;
};

type ReferenceSetImportDialogProps = {
  open: boolean;
  profileKey: string;
  platformId: string;
  remainingSlots: number;
  importing?: boolean;
  onClose: () => void;
  onImport: (items: ReferenceSetImportItem[]) => void | Promise<void>;
};

type ReferenceSetImportDraftItem = ReferenceSetImportItem & {
  id: string;
  previewUrl: string;
};

export const ReferenceSetImportDialog: React.FC<ReferenceSetImportDialogProps> = ({
  open,
  profileKey,
  platformId,
  remainingSlots,
  importing = false,
  onClose,
  onImport,
}) => {
  const [items, setItems] = useState<ReferenceSetImportDraftItem[]>([]);
  const [detectedProfileKey, setDetectedProfileKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => () => {
    revokePreviews(items);
  }, [items]);

  const replaceItems = (nextItems: ReferenceSetImportDraftItem[]) => {
    setItems((prev) => {
      revokePreviews(prev);
      return nextItems;
    });
    if (nextItems.length === 0) setDetectedProfileKey(null);
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
      .filter((file) => file.type.startsWith('image/'))
      .slice(0, Math.max(0, remainingSlots));
    event.target.value = '';
    const nextItems = files.map((file, index) => buildImportItem(profileKey, file, index));
    replaceItems(nextItems);
    const firstPreview = nextItems[0]?.previewUrl;
    if (firstPreview) {
      void detectProfileKey(firstPreview, nextItems.length).then(setDetectedProfileKey);
    }
  };

  const updateItem = (index: number, patch: Partial<BrandStyleKitReferenceDraft>) => {
    setItems((prev) => prev.map((item, itemIndex) => (
      itemIndex === index
        ? { ...item, draft: { ...item.draft, ...patch } }
        : item
    )));
  };

  const handleClose = () => {
    replaceItems([]);
    onClose();
  };

  const handleImport = async () => {
    await onImport(items.map(({ file, draft }) => ({ file, draft })));
    replaceItems([]);
  };

  return (
    <SDialog
      open={open}
      onClose={handleClose}
      title="Import reference set"
      maxWidth="lg"
      fullWidth
      data-testid={`platform-${platformId}-${profileKey}-reference-set-import-dialog`}
      primaryAction={{
        label: 'Import references',
        onClick: handleImport,
        loading: importing,
        disabled: items.length === 0 || remainingSlots <= 0,
        startIcon: <FileUploadOutlinedIcon />,
      }}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              Upload ordered carousel or single-image examples. The order becomes the reference manifest order.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {remainingSlots} of {BRAND_STYLE_REFERENCE_LIMIT} reference slots available for this profile.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={importing || remainingSlots <= 0}
          >
            Choose images
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFilesSelected}
            data-testid={`platform-${platformId}-${profileKey}-reference-set-file-input`}
          />
        </Stack>

        {remainingSlots <= 0 && (
          <Typography variant="body2" color="warning.main">
            This profile already has the maximum number of references.
          </Typography>
        )}
        {detectedProfileKey && (
          <Typography
            variant="caption"
            color={detectedProfileKey === profileKey ? 'text.secondary' : 'warning.main'}
          >
            Detected {getDesignProfileOption(detectedProfileKey).label}. Current slot is {getDesignProfileOption(profileKey).label}.
          </Typography>
        )}

        {items.length === 0 ? (
          <Box
            sx={{
              minHeight: 160,
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Select images to review roles and instructions before import.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {items.map((item, index) => (
              <Stack
                key={item.id}
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{
                  p: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Box
                  component="img"
                  src={item.previewUrl}
                  alt={item.draft.name || `Reference ${index + 1}`}
                  sx={{
                    width: { xs: '100%', md: 144 },
                    aspectRatio: '4 / 3',
                    objectFit: 'cover',
                    borderRadius: 0.75,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.default',
                    flex: '0 0 auto',
                  }}
                />
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                      label="Reference name"
                      size="small"
                      value={item.draft.name}
                      onChange={(event) => updateItem(index, { name: event.target.value })}
                      fullWidth
                      disabled={importing}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }} disabled={importing}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        value={item.draft.role}
                        onChange={(event) => updateItem(index, {
                          role: event.target.value as BrandStyleKitReferenceDraft['role'],
                          instruction: defaultReferenceInstruction(
                            profileKey,
                            index,
                            event.target.value as BrandStyleKitReferenceDraft['role'],
                          ),
                        })}
                      >
                        {REFERENCE_ROLES.map((role) => (
                          <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <TextField
                    label="Agent instruction"
                    size="small"
                    multiline
                    minRows={2}
                    value={item.draft.instruction}
                    onChange={(event) => updateItem(index, { instruction: event.target.value })}
                    fullWidth
                    disabled={importing}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {item.file.name} · {formatBytes(item.file.size)}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </SDialog>
  );
};

function buildImportItem(profileKey: string, file: File, index: number): ReferenceSetImportDraftItem {
  const role = defaultReferenceRole(profileKey, index);
  return {
    id: `${index}-${file.name}-${file.lastModified}`,
    file,
    previewUrl: URL.createObjectURL(file),
    draft: {
      url: '',
      mimeType: file.type || 'image/png',
      name: referenceSlotHint(profileKey, index),
      instruction: defaultReferenceInstruction(profileKey, index, role),
      role,
      textBudgets: [],
    },
  };
}

function revokePreviews(items: ReferenceSetImportDraftItem[]) {
  items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
}

function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function detectProfileKey(url: string, fileCount: number): Promise<string | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const ratio = image.naturalWidth / Math.max(1, image.naturalHeight);
      const isSeries = fileCount > 1;
      if (Math.abs(ratio - 1) < 0.08) {
        resolve(isSeries ? 'carousel_square' : 'single_image_square');
        return;
      }
      if (Math.abs(ratio - 0.8) < 0.08 || ratio < 0.75) {
        resolve(isSeries ? 'carousel_portrait' : 'single_image_portrait');
        return;
      }
      if (ratio > 1.25) {
        resolve(isSeries ? 'carousel_square' : 'single_image_landscape');
        return;
      }
      resolve(null);
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}
